/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  Form,
  Formik,
  FormikHelpers,
  FormikProps,
  useFormikContext,
} from 'formik'
import moment from 'moment'
import React, {
  JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { BiAccessibility, BiCake } from 'react-icons/bi'
import { FaRegIdCard } from 'react-icons/fa'
import {
  FiArrowRightCircle,
  FiEdit3,
  FiPlus,
  FiUser,
  FiUsers,
  FiX,
} from 'react-icons/fi'
import { MdDeleteForever, MdSecurity } from 'react-icons/md'

import usePersistedState from '../../hooks/usePersistedState'
import validaCPF from '../../utils/validaCPF'

import Button from '../../components/Button'
import Header from '../../components/Header'
import Input from '../../components/Input'
import InputHidden from '../../components/InputHidden'
import InputSelect from '../../components/InputSelect'
import ModalBox from '../../components/Modal'

import { Participant, UserData } from '../../utils/interfaces'
import { GrauParentescoList } from '../../utils/listasFixas'
import { Cpf, RgMask } from '../../utils/masks'

import { FileUploader } from '../../components/upload-file/file-uploader'
import { uploadFile } from '../../services/upload-file-to-aws'

type BeneficiaryFormValues = {
  addNew: boolean
  name: string
  cpf: string
  birthdate: string
  rg: string
  hasDeficiency: string
  deficiencyDcr: string
  grauParentesco: string
  selectedDocCode: string
}

const DOC_DEP_CPF = '14'
const DOC_DEP_RG = '15'
const DOC_DEP_CERT_NASC = '18'
const DOC_DEP_CERT_CAS = '19'

export type ParticipantAttachedDoc = {
  id: string // chave única
  docCode: string
  docLabel: string
  name: string
  type: string
  lastModified: number
  size: number
  url: string
}

export type ParticipantWithDocs = Participant & {
  attachedDocs?: ParticipantAttachedDoc[]
}

type DocumentItem = {
  codDocumento: string
  dcrDocumento: string
}

type DocumentFiles = {
  [codDocumento: string]: File[]
}

const fileKey = (f: File) => `${f.name}__${f.size}__${f.lastModified}`
const makeDocId = (docCode: string, f: File) =>
  `${docCode}__${f.name}__${f.lastModified}__${f.size}`

function normalizeText(s: unknown): string {
  return String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

function getParentescoLabel(value: string): string {
  return (
    GrauParentescoList.find((x) => String(x.value) === String(value))?.label ??
    ''
  )
}

function relationshipRules(grauParentescoValue: string): {
  allowCert: boolean
  certDocCode?: string
  certLabel?: string
} {
  const rawLabel = getParentescoLabel(grauParentescoValue)
  const label = normalizeText(rawLabel)

  // Filho / Enteado -> certidão de nascimento como alternativa
  if (label.includes('filh') || label.includes('entead')) {
    return {
      allowCert: true,
      certDocCode: DOC_DEP_CERT_NASC,
      certLabel: 'Certidão de nascimento',
    }
  }

  // Cônjuge / Companheiro(a) -> certidão de casamento como alternativa
  if (label.includes('conjuge') || label.includes('companheir')) {
    return {
      allowCert: true,
      certDocCode: DOC_DEP_CERT_CAS,
      certLabel: 'Certidão de casamento',
    }
  }

  // Outros -> sem certidão
  return { allowCert: false }
}

function docsStatusForParticipant(p: ParticipantWithDocs): {
  allowCert: boolean
  certLabel?: string
  hasCpfDoc: boolean
  hasRgDoc: boolean
  hasCert: boolean
  isOk: boolean
} {
  const rules = relationshipRules(p.grauParentesco ?? '')
  const docs = p.attachedDocs ?? []

  const hasCpfDoc = docs.some((d) => d.docCode === DOC_DEP_CPF)
  const hasRgDoc = docs.some((d) => d.docCode === DOC_DEP_RG)
  const hasCert =
    rules.allowCert && rules.certDocCode
      ? docs.some((d) => d.docCode === rules.certDocCode)
      : false

  const isOk = rules.allowCert
    ? (hasCpfDoc && hasRgDoc) || hasCert
    : hasCpfDoc && hasRgDoc

  return {
    allowCert: rules.allowCert,
    certLabel: rules.certLabel,
    hasCpfDoc,
    hasRgDoc,
    hasCert,
    isOk,
  }
}

function safeTrim(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

function isParticipantDataComplete(p: ParticipantWithDocs): boolean {
  const hasName = safeTrim(p.name).length > 0
  const hasCpf = safeTrim(p.cpf).length > 0
  const hasBirth = safeTrim(p.birthdate).length > 0
  const hasRg = safeTrim(p.rg).length > 0
  const hasGp = safeTrim(p.grauParentesco).length > 0
  const hasDef = safeTrim(p.hasDeficiency).length > 0

  const defIsYes = safeTrim(p.hasDeficiency).toUpperCase() === 'S'
  const hasDefDcr = defIsYes ? safeTrim(p.deficiencyDcr).length > 0 : true

  return hasName && hasCpf && hasBirth && hasRg && hasGp && hasDef && hasDefDcr
}

function DependentDocsSection({
  draftDocumentFiles,
  setDraftDocumentFiles,
  isUploading,
  setDraftError,
  onDraftAttachedListChange,
  onDraftOkChange,
}: {
  draftDocumentFiles: DocumentFiles
  setDraftDocumentFiles: React.Dispatch<React.SetStateAction<DocumentFiles>>
  isUploading: boolean
  setDraftError: (msg: string) => void
  onDraftAttachedListChange: (
    list: Array<{ docCode: string; docLabel: string; file: File }>,
  ) => void
  onDraftOkChange: (payload: {
    isOk: boolean
    allowCert: boolean
    certLabel?: string
  }) => void
}): JSX.Element {
  const { values, setFieldValue } = useFormikContext<BeneficiaryFormValues>()

  const rules = useMemo(
    () => relationshipRules(values.grauParentesco ?? ''),
    [values.grauParentesco],
  )

  const docOptions = useMemo<DocumentItem[]>(() => {
    const base: DocumentItem[] = [
      { codDocumento: DOC_DEP_CPF, dcrDocumento: 'CPF do dependente' },
      { codDocumento: DOC_DEP_RG, dcrDocumento: 'RG do dependente' },
    ]

    if (rules.allowCert && rules.certDocCode && rules.certLabel) {
      base.push({
        codDocumento: rules.certDocCode,
        dcrDocumento: `${rules.certLabel} do dependente`,
      })
    }

    return base
  }, [rules.allowCert, rules.certDocCode, rules.certLabel])

  useEffect(() => {
    const current = values.selectedDocCode
    const stillExists =
      !!current && docOptions.some((d) => d.codDocumento === current)
    if (stillExists) return

    const next = docOptions[0]?.codDocumento ?? ''
    setFieldValue('selectedDocCode', next, false)
    setDraftError('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docOptions])

  const selectedDocCode = values.selectedDocCode

  const selectedFiles = useMemo(() => {
    return draftDocumentFiles[selectedDocCode] ?? []
  }, [draftDocumentFiles, selectedDocCode])

  const setSelectedDocFiles = useCallback(
    (filesOrUpdater: File[] | ((prev: File[]) => File[])) => {
      if (!selectedDocCode) return

      setDraftDocumentFiles((prev) => {
        const current = prev[selectedDocCode] ?? []
        const rawNext =
          typeof filesOrUpdater === 'function'
            ? filesOrUpdater(current)
            : filesOrUpdater

        const one = rawNext?.[0] ? [rawNext[0]] : []

        const seen = new Set<string>()
        const next = one.filter((file) => {
          const k = fileKey(file)
          if (seen.has(k)) return false
          seen.add(k)
          return true
        })

        return { ...prev, [selectedDocCode]: next }
      })
    },
    [selectedDocCode, setDraftDocumentFiles],
  )

  const draftAttachedList = useMemo(() => {
    const items: Array<{ docCode: string; docLabel: string; file: File }> = []
    docOptions.forEach((doc) => {
      const files = draftDocumentFiles[doc.codDocumento] ?? []
      files.forEach((f) => {
        items.push({
          docCode: doc.codDocumento,
          docLabel: doc.dcrDocumento,
          file: f,
        })
      })
    })
    return items
  }, [docOptions, draftDocumentFiles])

  useEffect(() => {
    onDraftAttachedListChange(draftAttachedList)
  }, [draftAttachedList, onDraftAttachedListChange])

  const hasCpf = (draftDocumentFiles[DOC_DEP_CPF] ?? []).length > 0
  const hasRg = (draftDocumentFiles[DOC_DEP_RG] ?? []).length > 0
  const hasCert =
    rules.allowCert && rules.certDocCode
      ? (draftDocumentFiles[rules.certDocCode] ?? []).length > 0
      : false

  const isOk = useMemo(() => {
    if (rules.allowCert) return (hasCpf && hasRg) || hasCert
    return hasCpf && hasRg
  }, [hasCert, hasCpf, hasRg, rules.allowCert])

  useEffect(() => {
    onDraftOkChange({
      isOk,
      allowCert: rules.allowCert,
      certLabel: rules.certLabel,
    })
  }, [isOk, onDraftOkChange, rules.allowCert, rules.certLabel])

  const removeDraftItem = useCallback(
    (docCode: string) => {
      setDraftDocumentFiles((prev) => {
        const next = { ...prev }
        delete next[docCode]
        return next
      })
    },
    [setDraftDocumentFiles],
  )

  const selectedOptionLabel = useMemo(() => {
    return (
      docOptions.find((d) => d.codDocumento === selectedDocCode)
        ?.dcrDocumento ?? ''
    )
  }, [docOptions, selectedDocCode])

  return (
    <div className="w-full mt-2">
      <div className="flex flex-col tablet:flex-row tablet:items-end">
        <div className="w-full tablet:w-2/3">
          <InputSelect
            name="selectedDocCode"
            placeholder="Documento"
            options={docOptions.map((doc) => ({
              label: doc.dcrDocumento,
              value: doc.codDocumento,
            }))}
          />
        </div>
        <div className="text-xs text-[#333] ">
          <p className="text-[#C53030] font-semibold">
            {rules.allowCert
              ? `Obrigatório anexar CPF + RG ou ${rules.certLabel}.`
              : 'Obrigatório anexar CPF + RG.'}
          </p>
          <p className="text-[#C53030] ">Tamanho máximo: 5MB por arquivo.</p>
        </div>

        <div className="w-full tablet:w-1/3 flex flex-col items-center">
          <FileUploader
            files={selectedFiles}
            setFiles={setSelectedDocFiles}
            setFieldValue={() => {}}
            fieldName={`draft.docs.${selectedDocCode}`}
          >
            <FileUploader.Button
              disabled={!selectedDocCode || isUploading}
              className="min-w-fit"
              color="green"
              width="small"
              fontSize="small"
            >
              <div className="flex flex-row gap-3 font-semibold text-[14px] items-center justify-center">
                <FiPlus size={20} />
                Anexar{' '}
                {selectedOptionLabel
                  ? selectedOptionLabel.split(' do dependente')[0]
                  : 'documento'}
              </div>
            </FileUploader.Button>
          </FileUploader>
        </div>
      </div>

      {draftAttachedList.length > 0 && (
        <div className="mt-3">
          <div className="flex flex-col gap-2">
            {draftAttachedList.map((item) => (
              <div
                key={`${item.docCode}__${item.file.name}__${item.file.lastModified}__${item.file.size}`}
                className="flex flex-col tablet:flex-row tablet:items-center tablet:justify-between gap-2 rounded-md p-3"
              >
                <div className="flex flex-wrap justify-between w-full">
                  <div className="min-w-0">
                    <p
                      className="text-sm font-semibold truncate"
                      title={item.file.name}
                    >
                      {item.file.name}
                    </p>
                    <p
                      className="text-xs text-tertiary-3 truncate"
                      title={item.docLabel}
                    >
                      {item.docLabel} • {item.file.type || 'tipo desconhecido'}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="inline-flex items-center gap-2 border px-3 py-1 rounded-full hover:border-[#C53030] hover:text-[#C53030]"
                    onClick={() => removeDraftItem(item.docCode)}
                    title="Remover"
                    disabled={isUploading}
                  >
                    <MdDeleteForever size={18} />
                    Remover
                  </button>
                </div>
                <hr />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const Beneficiary: React.FC = () => {
  const [userData] = usePersistedState<UserData>('userData', {} as UserData)

  const [participants, setParticipants] = usePersistedState<
    ParticipantWithDocs[]
  >('participantsGroup', [])

  const navigate = useNavigate()
  const location = useLocation()
  const formikRef = useRef<FormikProps<BeneficiaryFormValues>>(null)

  const benefFormBoxRef = useRef<HTMLDivElement | null>(null)
  const shouldScrollToBenefBoxRef = useRef(false)

  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [draftDocumentFiles, setDraftDocumentFiles] = useState<DocumentFiles>(
    {},
  )
  const [draftAttachedList, setDraftAttachedList] = useState<
    Array<{ docCode: string; docLabel: string; file: File }>
  >([])
  const [draftStatus, setDraftStatus] = useState<{
    isOk: boolean
    allowCert: boolean
    certLabel?: string
  }>({
    isOk: false,
    allowCert: false,
  })
  const [draftDocsError, setDraftDocsError] = useState('')
  const [isUploadingDraft, setIsUploadingDraft] = useState(false)

  // ✅ CPF “base” do participante que está sendo editado (pra não acusar duplicado dele mesmo)
  const editingCpfBase = useMemo(() => {
    if (editingIndex === null) return ''
    return (participants[editingIndex]?.cpf ?? '').replace(/\D/g, '')
  }, [editingIndex, participants])

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .required('Campo obrigatório.')
          .matches(/\s/g, 'Digite o nome completo')
          .min(3, 'Digite o nome completo'),

        birthdate: Yup.string()
          .required('Campo obrigatório.')
          .test(
            '',
            'A data de nascimento não pode ser maior que hoje.',
            (value) =>
              moment() > moment((value ?? '').split('/').reverse().join('-')) ||
              value === '',
          )
          .test(
            '',
            'Data de nascimento inválida',
            (value) =>
              moment((value ?? '').split('/').reverse().join('-')).isValid() ||
              value === '',
          ),

        cpf: Yup.string()
          .required('Campo obrigatório.')
          .test('', 'CPF inválido', (value) => {
            const cpf = value ?? ''
            return (
              validaCPF(cpf.replace(/\./g, '').replace('-', '')) || cpf === ''
            )
          })
          .test('', 'CPF já utilizado em outro cadastro', (value) => {
            const cpfClean = (value ?? '').replace(/\./g, '').replace(/-/g, '')
            if (!cpfClean) return true

            // não pode ser o CPF do servidor
            if ((userData?.cpf ?? '') === cpfClean) return false

            // quando editando: permite se for o mesmo CPF do próprio registro
            if (editingIndex !== null && cpfClean === editingCpfBase)
              return true

            // senão, bloqueia se já existir em outro participante
            const exists = participants.some(
              (p) => (p.cpf ?? '').replace(/\D/g, '') === cpfClean,
            )
            return !exists
          }),

        rg: Yup.string().required('Campo obrigatório.'),
        grauParentesco: Yup.string().required('Campo obrigatório'),
        hasDeficiency: Yup.string().required('Campo obrigatório'),
        deficiencyDcr: Yup.string().when('hasDeficiency', {
          is: (v: string) =>
            ['sim', 's', 'true', '1', 'S'].includes(normalizeText(v)),
          then: (schema) => schema.required('Descreva a incapacidade'),
          otherwise: (schema) => schema.notRequired(),
        }),
        selectedDocCode: Yup.string().notRequired(),
      }),
    [participants, userData?.cpf, editingIndex, editingCpfBase],
  )

  const initialValues = useMemo<BeneficiaryFormValues>(() => {
    const p = editingIndex !== null ? participants[editingIndex] : undefined

    return {
      addNew: false,
      name: p?.name ?? '',
      cpf: p?.cpf ? Cpf(p.cpf) : '',
      birthdate: p?.birthdate ? p.birthdate.split('-').reverse().join('/') : '',
      rg: p?.rg ?? '',
      hasDeficiency: p?.hasDeficiency ?? '',
      deficiencyDcr: p?.deficiencyDcr ?? '',
      grauParentesco: p?.grauParentesco ?? '',
      selectedDocCode: DOC_DEP_CPF,
    }
  }, [editingIndex, participants])

  const emptyValues = useMemo<BeneficiaryFormValues>(
    () => ({
      addNew: false,
      name: '',
      cpf: '',
      birthdate: '',
      rg: '',
      hasDeficiency: '',
      deficiencyDcr: '',
      grauParentesco: '',
      selectedDocCode: DOC_DEP_CPF,
    }),
    [],
  )

  const scrollToBenefBox = useCallback(() => {
    const el =
      benefFormBoxRef.current ||
      (document.getElementById('benefFormBox') as HTMLDivElement | null)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const clearDraft = useCallback(() => {
    setDraftDocumentFiles({})
    setDraftAttachedList([])
    setDraftDocsError('')
    setDraftStatus({ isOk: false, allowCert: false })
  }, [])

  const makeStoredDoc = useCallback(
    async (
      docCode: string,
      docLabel: string,
      file: File,
    ): Promise<ParticipantAttachedDoc> => {
      const url = await uploadFile({ file })
      return {
        id: makeDocId(docCode, file),
        docCode,
        docLabel,
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        size: file.size,
        url,
      }
    },
    [],
  )

  const handleEdit = useCallback(
    (index: number) => {
      setEditingIndex(index)
      clearDraft()
      shouldScrollToBenefBoxRef.current = true
    },
    [clearDraft],
  )

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null)
    clearDraft()
    formikRef.current?.resetForm()
  }, [clearDraft])

  const handleRemove = useCallback(
    (index: number) => {
      setParticipants((prev) => prev.filter((_, i) => i !== index))
      setEditingIndex((prev) => (prev === index ? null : prev))
    },
    [setParticipants],
  )

  const handleSubmit = useCallback(
    async (
      formData: BeneficiaryFormValues,
      helpers: FormikHelpers<BeneficiaryFormValues>,
    ) => {
      const dataForm = formData.birthdate.split('/').reverse().join('-')

      const nextParticipantBase: ParticipantWithDocs = {
        name: formData.name,
        birthdate: formData.birthdate === '' ? '' : dataForm,
        cpf: formData.cpf?.replace(/\./g, '').replace(/-/g, ''),
        rg: formData.rg?.replace(/\./g, '').replace(/-/g, ''),
        hasDeficiency: formData.hasDeficiency,
        deficiencyDcr: formData.deficiencyDcr,
        grauParentesco: formData.grauParentesco,
      } as ParticipantWithDocs

      const existingDocs =
        editingIndex !== null
          ? (participants[editingIndex]?.attachedDocs ?? [])
          : []

      const rules = relationshipRules(formData.grauParentesco ?? '')
      const existingHasCpf = existingDocs.some((d) => d.docCode === DOC_DEP_CPF)
      const existingHasRg = existingDocs.some((d) => d.docCode === DOC_DEP_RG)
      const existingHasCert =
        rules.allowCert && rules.certDocCode
          ? existingDocs.some((d) => d.docCode === rules.certDocCode)
          : false

      const existingOk = rules.allowCert
        ? (existingHasCpf && existingHasRg) || existingHasCert
        : existingHasCpf && existingHasRg

      const willBeOk = existingOk || draftStatus.isOk

      if (!willBeOk) {
        setDraftDocsError(
          rules.allowCert
            ? `Anexe CPF + RG ou ${rules.certLabel} para salvar este dependente.`
            : 'Anexe CPF + RG para salvar este dependente.',
        )
        return
      }

      setDraftDocsError('')
      setIsUploadingDraft(true)

      try {
        const uploads: ParticipantAttachedDoc[] = []

        for (const item of draftAttachedList) {
          try {
            const uploadedDoc = await makeStoredDoc(
              item.docCode,
              item.docLabel,
              item.file,
            )
            uploads.push(uploadedDoc)
          } catch (err: unknown) {
            setDraftDocumentFiles((prev) => {
              const next = { ...prev }
              delete next[item.docCode]
              return next
            })

            const msg =
              err instanceof Error ? err.message : 'Falha ao enviar documentos.'

            setDraftDocsError(msg)
            return
          }
        }

        const docsMap = new Map<string, ParticipantAttachedDoc>()
        ;[...existingDocs, ...uploads].forEach((d) => docsMap.set(d.id, d))

        const nextParticipant: ParticipantWithDocs = {
          ...nextParticipantBase,
          attachedDocs: Array.from(docsMap.values()),
        }

        setParticipants((prev) => {
          if (editingIndex === null) return [...prev, nextParticipant]
          return prev.map((p, i) =>
            i === editingIndex ? { ...p, ...nextParticipant } : p,
          )
        })

        if (editingIndex !== null) {
          setEditingIndex(null)
          clearDraft()
          helpers.resetForm({ values: emptyValues })
          navigate(location.pathname)
          return
        }

        if (formData.addNew) {
          setEditingIndex(null)
          clearDraft()
          shouldScrollToBenefBoxRef.current = true
          helpers.resetForm({ values: emptyValues })
          navigate(location.pathname)
        } else {
          setEditingIndex(null)
          clearDraft()
          navigate('/service-time')
        }
      } finally {
        setIsUploadingDraft(false)
      }
    },
    [
      clearDraft,
      draftAttachedList,
      draftStatus.isOk,
      editingIndex,
      emptyValues,
      initialValues,
      location.pathname,
      makeStoredDoc,
      navigate,
      participants,
      setParticipants,
      location.pathname,
    ],
  )

  // ✅ botão principal coerente com "Adicionar" vs "Salvar alterações"
  const handlePrimaryAction = useCallback(() => {
    const isEditing = editingIndex !== null

    // se está editando, NÃO é addNew
    formikRef.current?.setFieldValue('addNew', !isEditing, false)
    formikRef.current?.submitForm()
  }, [editingIndex])

  useEffect(() => {
    if (!shouldScrollToBenefBoxRef.current) return
    const t = window.setTimeout(() => {
      scrollToBenefBox()
      shouldScrollToBenefBoxRef.current = false
    }, 50)
    return () => window.clearTimeout(t)
  }, [editingIndex, scrollToBenefBox])

  const canProceed = useMemo(() => {
    if (participants.length === 0) return true
    return participants.every((p) => {
      const dataOk = isParticipantDataComplete(p)
      const docsOk = docsStatusForParticipant(p).isOk
      return dataOk && docsOk
    })
  }, [participants])

  const missingList = useMemo(() => {
    const issues: Array<{ index: number; msg: string }> = []
    participants.forEach((p, index) => {
      const dataOk = isParticipantDataComplete(p)
      const docs = docsStatusForParticipant(p)

      if (!dataOk) issues.push({ index, msg: 'Dados obrigatórios pendentes' })
      if (!docs.isOk) {
        issues.push({
          index,
          msg: docs.allowCert
            ? `Docs pendentes (CPF + RG ou ${docs.certLabel})`
            : 'Docs pendentes (CPF + RG)',
        })
      }
    })
    return issues
  }, [participants])

  const removeDocFromParticipant = useCallback(
    (participantIndex: number, docId: string) => {
      setParticipants((prev) =>
        prev.map((p, i) => {
          if (i !== participantIndex) return p
          const nextDocs = (p.attachedDocs ?? []).filter((d) => d.id !== docId)
          return { ...p, attachedDocs: nextDocs }
        }),
      )
    },
    [setParticipants],
  )

  return (
    <>
      <Header />

      <div className="mx-auto mb-[15px] flex max-w-[600px] flex-col items-center px-[5px] max-[766px]:max-w-[550px]">
        <div className="mx-[5px] mb-[20px] w-full rounded-[8px] bg-white p-[30px_25px] shadow-[0px_2px_4px_rgba(0,0,0,0.05),0px_6px_12px_rgba(61,69,67,0.05)] max-[766px]:p-[20px_15px]">
          <div className="w-full">
            {participants.length > 0 ? (
              <div className="relative my-[10px] w-full p-[15px_25px_10px] max-[766px]:p-[20px_15px_10px]">
                <h3 className="mb-[15px] flex justify-center text-center text-[18px] font-bold text-[#0071ba]">
                  Dependentes
                </h3>

                {participants.map((participant, index) => {
                  const isEditing = editingIndex === index

                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={index} className="relative w-full">
                      <div className="relative">
                        <div className="my-[6px]">
                          <label>Nome: </label>
                          <strong>{participant.name}</strong>
                        </div>

                        <div className="my-[6px]">
                          <label>CPF: </label>
                          <strong>{Cpf(participant.cpf)}</strong>
                        </div>

                        <div className="my-[6px]">
                          <label>RG: </label>
                          <strong>
                            {RgMask(participant.rg) || 'não informado'}
                          </strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Data de nascimento: </label>
                          <strong>
                            {(participant.birthdate ?? '').includes('-')
                              ? participant.birthdate
                                  .split('-')
                                  .reverse()
                                  .join('/')
                              : participant.birthdate}
                          </strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Parentesco: </label>
                          <strong>
                            {
                              GrauParentescoList.filter(
                                (v) =>
                                  String(v.value) ===
                                  String(participant.grauParentesco),
                              )[0]?.label
                            }
                          </strong>
                        </div>

                        <div className="my-[6px]">
                          <label>Possui incapacidade: </label>
                          {!participant.hasDeficiency ? (
                            <strong className="text-red-600">
                              Não informado
                            </strong>
                          ) : (
                            <strong>
                              {participant.hasDeficiency === 'S'
                                ? 'Sim'
                                : 'Não'}
                            </strong>
                          )}
                        </div>

                        {participant.hasDeficiency === 'S' && (
                          <div className="my-[6px]">
                            <label>Incapacidade: </label>
                            {participant.deficiencyDcr ? (
                              <strong>{participant.deficiencyDcr}</strong>
                            ) : (
                              <strong className="text-red-600">
                                Não informado
                              </strong>
                            )}
                          </div>
                        )}

                        <div className="my-[6px]">
                          <label>Documentos anexados: </label>
                          {(participant.attachedDocs?.length ?? 0) > 0 ? (
                            <div className="px-2">
                              {(participant.attachedDocs ?? []).map((doc) => (
                                <div
                                  key={doc.id}
                                  className="flex items-center justify-between gap-2 py-1"
                                >
                                  <strong
                                    className="truncate"
                                    title={`${doc.docLabel} | ${doc.name}`}
                                  >
                                    {doc.docLabel} | {doc.name}
                                  </strong>

                                  <button
                                    type="button"
                                    className="inline-flex items-center justify-center rounded-full border px-2 py-1 hover:border-[#C53030] hover:text-[#C53030]"
                                    title="Remover documento"
                                    onClick={() =>
                                      removeDocFromParticipant(index, doc.id)
                                    }
                                    disabled={isUploadingDraft}
                                  >
                                    <FiX size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <strong className="text-red-600">
                              Não anexado
                            </strong>
                          )}
                        </div>
                      </div>

                      <div className="w-full flex justify-center mt-4 gap-3 flex-wrap">
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 border px-3 py-1 rounded-full hover:border-[#0071ba] hover:text-[#0071ba]"
                          onClick={() => handleEdit(index)}
                          aria-label={`Editar ${index}`}
                          title="Editar"
                          disabled={isUploadingDraft}
                        >
                          <FiEdit3 size={18} />
                          {isEditing ? 'Editando...' : 'Editar'}
                        </button>

                        <button
                          type="button"
                          className="inline-flex items-center gap-2 border px-3 py-1 rounded-full hover:border-[#C53030] hover:text-[#C53030]"
                          onClick={() => handleRemove(index)}
                          aria-label={`Remover ${index}`}
                          title="Remover"
                          disabled={isUploadingDraft}
                        >
                          <MdDeleteForever size={18} />
                          Remover
                        </button>
                      </div>

                      <div className="my-[10px] h-[1px] w-full bg-[#E5E7EB]" />
                    </div>
                  )
                })}
              </div>
            ) : null}
          </div>

          <h3
            id="benefFormBox"
            ref={benefFormBoxRef}
            className="mb-[15px] flex justify-center text-center text-[18px] font-bold text-[#0071ba]"
          >
            {editingIndex !== null
              ? 'Editar dependente'
              : 'Informe seus dependentes:'}
          </h3>

          <Formik
            innerRef={formikRef}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize
            validateOnBlur
            validateOnChange={false}
          >
            {({ values }) => (
              <Form className="flex w-full flex-col items-center">
                <InputHidden name="addNew" type="hidden" />

                <Input
                  placeholder="Nome completo"
                  name="name"
                  icon={FiUser}
                  disabled={isUploadingDraft}
                />

                <Input
                  placeholder="CPF"
                  name="cpf"
                  icon={MdSecurity}
                  type="tel"
                  mask="cpf"
                  disabled={isUploadingDraft}
                />

                <Input
                  icon={FaRegIdCard}
                  name="rg"
                  mask="rg"
                  placeholder="RG (apenas números)"
                  disabled={isUploadingDraft}
                />

                <Input
                  icon={BiCake}
                  name="birthdate"
                  mask="date"
                  placeholder="Data de nascimento"
                  disabled={isUploadingDraft}
                />

                <InputSelect
                  icon={FiUsers}
                  name="grauParentesco"
                  options={GrauParentescoList}
                  placeholder="Grau de parentesco"
                  isDisabled={isUploadingDraft}
                />

                <InputSelect
                  icon={BiAccessibility}
                  name="hasDeficiency"
                  placeholder="Possui alguma incapacidade?"
                  options={[
                    { label: 'Sim', value: 'S' },
                    { label: 'Não', value: 'N' },
                  ]}
                  isDisabled={isUploadingDraft}
                />

                {values.hasDeficiency === 'S' && (
                  <Input
                    icon={FiEdit3}
                    name="deficiencyDcr"
                    placeholder="Descreva a incapacidade"
                    disabled={isUploadingDraft}
                  />
                )}

                <DependentDocsSection
                  draftDocumentFiles={draftDocumentFiles}
                  setDraftDocumentFiles={setDraftDocumentFiles}
                  isUploading={isUploadingDraft}
                  setDraftError={setDraftDocsError}
                  onDraftAttachedListChange={setDraftAttachedList}
                  onDraftOkChange={setDraftStatus}
                />
              </Form>
            )}
          </Formik>

          <hr className="my-4" />

          <div className="flex flex-col items-center w-full justify-center flex-wrap">
            <Button
              type="button"
              color="blue"
              className="min-w-fit gap-3 px-10"
              onClick={handlePrimaryAction}
              disabled={isUploadingDraft}
              title={
                !draftStatus.isOk
                  ? draftStatus.allowCert
                    ? `Anexe CPF + RG ou ${draftStatus.certLabel} para habilitar.`
                    : 'Anexe CPF + RG para habilitar.'
                  : undefined
              }
            >
              <FiPlus size={24} />
              {isUploadingDraft
                ? 'Enviando...'
                : editingIndex !== null
                  ? 'Salvar alterações'
                  : 'Adicionar dependente'}
            </Button>

            {editingIndex !== null && (
              <button
                type="button"
                className="inline-flex w-fit px-3 py-1 items-center gap-2 border rounded-full hover:border-[#C53030] hover:text-[#C53030]"
                onClick={handleCancelEdit}
                title="Cancelar edição"
                disabled={isUploadingDraft}
              >
                <FiX size={18} />
                Cancelar edição
              </button>
            )}
          </div>
        </div>

        {participants.length > 0 ? (
          <>
            {!canProceed && (
              <div className="mb-3 text-xs text-[#C53030] text-center max-w-[520px]">
                <p className="font-semibold">
                  Complete os dados e anexe os documentos obrigatórios de todos
                  os dependentes para continuar.
                </p>
                <div className="mt-2 text-left">
                  {missingList.map((x) => (
                    <p key={`${x.index}-${x.msg}`}>
                      • Dependente #{x.index + 1}: {x.msg}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <Button
              type="button"
              fontSize="normal"
              color="blue"
              className="min-w-fit gap-3 px-10"
              disabled={!canProceed || isUploadingDraft}
              onClick={() => {
                if (!canProceed) return
                navigate('/service-time')
              }}
            >
              Avançar
              <FiArrowRightCircle size={24} />
            </Button>
          </>
        ) : (
          <Button
            type="button"
            fontSize="normal"
            color="pink"
            className="min-w-fit gap-3 px-10"
            onClick={() => navigate('/service-time')}
            disabled={isUploadingDraft}
          >
            Não tenho dependentes
            <FiArrowRightCircle size={24} />
          </Button>
        )}

        <button
          type="button"
          onClick={() => navigate('/personal-info')}
          className="mb-[15px] flex flex-col items-center justify-center border-0 border-b border-transparent bg-transparent text-[#333] hover:border-[#333] focus:border-[#333]"
          disabled={isUploadingDraft}
        >
          &lt; Anterior
        </button>

        <ModalBox
          isOpen={!!draftDocsError}
          onRequestClose={() => setDraftDocsError('')}
        >
          <p>{draftDocsError}</p>
        </ModalBox>
      </div>
    </>
  )
}

export default Beneficiary
